<div class="website-content" style="max-width:72rem;padding:0 var(--wa-spacing-x-large) var(--wa-spacing-x-large) var(--wa-spacing-x-large);">
	<div class="wa-stack wa-gap-xl">
		<wa-button [routerLink]="['/plugins']" appearance="plain" variant="neutral">Back to Plugins</wa-button>
		<wa-card appearance="outlined">
			<div class="wa-stack wa-gap-m">
				<div class="wa-stack wa-gap-s">
					<h1 waHeading="xl" waText>JWebMP Core</h1>
					<p waBody="m" waText>Strongly-typed HTML/CSS/JS component model &mdash; the entire HTML5 element set as Java classes with CRTP fluent APIs, 50+ server-driven event adapters, typed CSS builder, dual-mode rendering &#40;HTML and JSON&#41;, and SPI-driven extensibility. Every component serves dual purposes: toString&#40;true&#41; renders as full HTML with all CSS/JS, toString&#40;&#41; renders as JSON for AJAX/API payloads.</p>
					<div class="wa-cluster wa-gap-xs">
						<wa-tag pill variant="neutral">Java 25+</wa-tag>
						<wa-tag pill variant="neutral">Guice 7</wa-tag>
						<wa-tag pill variant="neutral">Vert.x 5</wa-tag>
						<wa-tag pill variant="neutral">Maven 4</wa-tag>
						<wa-tag pill variant="neutral">JPMS Modular</wa-tag>
					</div>
				</div>
				<wa-divider></wa-divider>
				<div class="wa-stack wa-gap-xs">
					<div waCaption="s" waFontWeight="semibold" waText>COORDINATES &amp; MODULE</div>
					<p waBody="s" waText>Maven: com.jwebmp.core:core</p>
					<p waBody="s" waText>JPMS Module: com.jwebmp.core</p>
					<p waBody="s" waText>Built on: JWebMP 2.0.0-SNAPSHOT</p>
				</div>
				<wa-details summary="Installation">
					<div class="wa-stack wa-gap-xs">
						@if (!useGradle) {
							<div class="code-block-wrapper">
								<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>Maven</div>
								<markdown [data]="mdContent_547" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							</div>
						}
						@if (useGradle) {
							<div class="code-block-wrapper">
								<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>Gradle &#40;Kotlin DSL&#41;</div>
								<markdown [data]="mdContent_548" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							</div>
						}
					</div>
				</wa-details>
				<wa-divider></wa-divider>
				<div class="wa-stack wa-gap-m">
					<h2 waHeading="l" waText>Components &#40;7&#41;</h2>
					<p waBody="m" waText>Click any component to see its full API &mdash; properties, constructors, methods, events, slots, and examples.</p>
					<div class="wa-grid wa-gap-s" style="--min-column-size:18rem;">
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>Page</h4>
								<p waBody="s" waColorText="quiet" waText>Top-level HTML page container with head, body, CSS/JS queues, and rendering pipeline</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">4 props</wa-tag>
									<wa-tag pill variant="neutral">6 methods</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/page']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>DivSimple</h4>
								<p waBody="s" waColorText="quiet" waText>Simplified div element for quick component authoring without generics complexity</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">2 props</wa-tag>
									<wa-tag pill variant="neutral">4 methods</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/div-simple']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>Paragraph</h4>
								<p waBody="s" waColorText="quiet" waText>HTML paragraph element with text alignment and styling</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">2 props</wa-tag>
									<wa-tag pill variant="neutral">2 methods</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/paragraph']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>Table</h4>
								<p waBody="s" waColorText="quiet" waText>HTML table with typed header, body, and footer sections</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">1 props</wa-tag>
									<wa-tag pill variant="neutral">4 methods</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/table']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>Form</h4>
								<p waBody="s" waColorText="quiet" waText>HTML form element with action, method, and input bindings</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">3 props</wa-tag>
									<wa-tag pill variant="neutral">4 methods</wa-tag>
									<wa-tag pill variant="warning">1 events</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/form']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>InputTextType</h4>
								<p waBody="s" waColorText="quiet" waText>Typed text input element with placeholder, value, and validation</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">6 props</wa-tag>
									<wa-tag pill variant="neutral">6 methods</wa-tag>
									<wa-tag pill variant="warning">2 events</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/input-text-type']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
						<wa-card appearance="outlined">
							<div class="wa-stack wa-gap-xs">
								<h4 waHeading="s" waText>Feature</h4>
								<p waBody="s" waColorText="quiet" waText>Wraps JavaScript libraries with typed options, CSS/JS references, and render hooks</p>
								<div class="wa-cluster wa-gap-xs">
									<wa-tag pill variant="brand">3 props</wa-tag>
									<wa-tag pill variant="neutral">5 methods</wa-tag>
									<wa-tag pill variant="success">1 examples</wa-tag>
								</div>
								<wa-button [routerLink]="['/plugins/core/feature']" appearance="plain" variant="brand">View Details &rarr;</wa-button>
							</div>
						</wa-card>
					</div>
				</div>
				<wa-details summary="Features (11)">
					<div class="wa-stack wa-gap-xs">
						<p waBody="s" waColorText="quiet" waText>Complete HTML5 element library &mdash; every element &#40;Div, Span, Table, Form, Input, Select, Canvas, Video, Article, Section, ...&#41; as typed Java class with CRTP fluent API</p>
						<p waBody="s" waColorText="quiet" waText>Dual rendering modes &mdash; toString&#40;true&#41; for full HTML with CSS/JS, toString&#40;&#41; for JSON serialization</p>
						<p waBody="s" waColorText="quiet" waText>Typed input elements &mdash; InputTextType, InputEmailType, InputNumberType, InputDateType, InputFileType, InputCheckBoxType, InputRadioType, and 15 more</p>
						<p waBody="s" waColorText="quiet" waText>Typed CSS builder &mdash; annotation-driven CSS via &#64;CSS across 14 sub-packages: backgrounds, borders, colours, fonts, margins, padding, displays, lists, tables, text, outline, measurement, height/width, image</p>
						<p waBody="s" waColorText="quiet" waText>50+ server-driven event adapters &mdash; OnClickAdapter, OnChangeAdapter, OnSubmitAdapter, OnDragAdapter, OnKeyDownAdapter, OnMouseEnterAdapter, and more</p>
						<p waBody="s" waColorText="quiet" waText>Page configurators &mdash; IPageConfigurator SPI for injecting CSS links, JS references, dynamic scripts before rendering</p>
						<p waBody="s" waColorText="quiet" waText>AJAX pipeline &mdash; AjaxCall/AjaxResponse carry event payloads and DOM update instructions between browser and server</p>
						<p waBody="s" waColorText="quiet" waText>Feature system &mdash; Feature wraps JavaScript libraries with typed options, CSS/JS references, and automatic dependency ordering</p>
						<p waBody="s" waColorText="quiet" waText>Content Security Policy builder for CSP headers</p>
						<p waBody="s" waColorText="quiet" waText>11-layer deep CRTP component hierarchy: ComponentBase through ComponentEventBase</p>
						<p waBody="s" waColorText="quiet" waText>SPI-driven extension via ServiceLoader</p>
					</div>
				</wa-details>
				<wa-divider></wa-divider>
				<wa-details open summary="Examples (2)">
					<div class="wa-stack wa-gap-m">
						<div class="wa-stack wa-gap-xs">
							<h4 waHeading="s" waText>Create a Page with Components</h4>
							<p waBody="s" waColorText="quiet" waText>Build a full HTML page with nested div, heading, and paragraph.</p>
							<markdown [data]="mdContent_549" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
						</div>
						<div class="wa-stack wa-gap-xs">
							<h4 waHeading="s" waText>Server-Driven Click Event</h4>
							<p waBody="s" waColorText="quiet" waText>Attach a server-side click handler that runs Java code when the button is clicked.</p>
							<markdown [data]="mdContent_550" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
						</div>
					</div>
				</wa-details>
				<wa-details open summary="Quick Start">
					<markdown [data]="mdContent_551" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
				</wa-details>
				<wa-divider></wa-divider>
				<wa-details open summary="SPI Extension Point Details (5)">
					<div class="wa-stack wa-gap-s">
						<div class="wa-stack wa-gap-xs">
							<div class="wa-cluster wa-gap-xs">
								<wa-tag pill variant="brand">IPageConfigurator</wa-tag>
								<wa-tag pill variant="neutral">uses</wa-tag>
							</div>
							<p waBody="s" waColorText="quiet" waText>Discovered at startup to inject CSS/JS references and configure the page before rendering</p>
						</div>
						<div class="wa-stack wa-gap-xs">
							<div class="wa-cluster wa-gap-xs">
								<wa-tag pill variant="brand">IPage</wa-tag>
								<wa-tag pill variant="neutral">uses</wa-tag>
							</div>
							<p waBody="s" waColorText="quiet" waText>Page implementations discovered via ServiceLoader for automatic route registration</p>
						</div>
						<div class="wa-stack wa-gap-xs">
							<div class="wa-cluster wa-gap-xs">
								<wa-tag pill variant="success">IGuiceModule</wa-tag>
								<wa-tag pill variant="neutral">provides</wa-tag>
							</div>
							<p waBody="s" waColorText="quiet" waText>Registers core Guice bindings for component model, event system, and rendering pipeline</p>
						</div>
						<div class="wa-stack wa-gap-xs">
							<div class="wa-cluster wa-gap-xs">
								<wa-tag pill variant="success">IOnComponentConfigured</wa-tag>
								<wa-tag pill variant="neutral">provides</wa-tag>
							</div>
							<p waBody="s" waColorText="quiet" waText>Callback fired after each component is configured, enabling post-processing hooks</p>
						</div>
						<div class="wa-stack wa-gap-xs">
							<div class="wa-cluster wa-gap-xs">
								<wa-tag pill variant="brand">ScriptProvider</wa-tag>
								<wa-tag pill variant="neutral">uses</wa-tag>
							</div>
							<p waBody="s" waColorText="quiet" waText>Provides dynamic JavaScript fragments injected into the page at render time</p>
						</div>
					</div>
				</wa-details>
				<wa-details open summary="SPI Extension Points">
					<div class="wa-stack wa-gap-xs">
						<div class="wa-cluster wa-gap-xs">
							<wa-tag pill variant="success">provides IGuiceScanModuleInclusions</wa-tag>
							<wa-tag pill variant="success">provides IGuiceModule</wa-tag>
							<wa-tag pill variant="success">provides IGuicePreStartup</wa-tag>
							<wa-tag pill variant="success">provides IGuicePostStartup</wa-tag>
							<wa-tag pill variant="success">provides IOnComponentConfigured</wa-tag>
						</div>
						<div class="wa-cluster wa-gap-xs">
							<wa-tag pill variant="brand">uses IPageConfigurator</wa-tag>
							<wa-tag pill variant="brand">uses IPage</wa-tag>
							<wa-tag pill variant="brand">uses IOnClickService</wa-tag>
							<wa-tag pill variant="brand">uses IOnChangeService</wa-tag>
							<wa-tag pill variant="brand">uses IOnSubmitService</wa-tag>
							<wa-tag pill variant="brand">uses ScriptProvider</wa-tag>
						</div>
					</div>
				</wa-details>
				<wa-divider></wa-divider>
				<wa-details open summary="Properties (2)">
					<table style="border-collapse:collapse;width:100%;">
						<thead>
							<tr style="border-bottom:2px solid var(--wa-color-neutral-200);">
								<th style="color:var(--wa-color-text-quiet);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);text-align:left;">Property</th>
								<th style="color:var(--wa-color-text-quiet);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);text-align:left;">Type</th>
								<th style="color:var(--wa-color-text-quiet);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);text-align:left;">Default</th>
								<th style="color:var(--wa-color-text-quiet);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);text-align:left;">Description</th>
							</tr>
						</thead>
						<tbody>
							<tr style="border-bottom:1px solid var(--wa-color-neutral-100);">
								<td style="color:var(--wa-color-brand-normal);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);">Page.setTiny(boolean)</td>
								<td style="color:var(--wa-color-text-quiet);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);padding:var(--wa-spacing-small);">boolean</td>
								<td style="color:var(--wa-color-text-quiet);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);padding:var(--wa-spacing-small);">false</td>
								<td style="font-size:var(--wa-font-size-s);padding:var(--wa-spacing-small);">Enable minified HTML output — removes whitespace and comments</td>
							</tr>
							<tr style="border-bottom:1px solid var(--wa-color-neutral-100);">
								<td style="color:var(--wa-color-brand-normal);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);font-weight:var(--wa-font-weight-semibold);padding:var(--wa-spacing-small);">Page.setRunningLocalhost(boolean)</td>
								<td style="color:var(--wa-color-text-quiet);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);padding:var(--wa-spacing-small);">boolean</td>
								<td style="color:var(--wa-color-text-quiet);font-family:var(--wa-font-mono);font-size:var(--wa-font-size-xs);padding:var(--wa-spacing-small);">true</td>
								<td style="font-size:var(--wa-font-size-s);padding:var(--wa-spacing-small);">Controls whether to serve from localhost or remote address</td>
							</tr>
						</tbody>
					</table>
				</wa-details>
				<wa-details open summary="Prerequisites">
					<div class="wa-cluster wa-gap-xs">
						<wa-tag pill variant="neutral">Java 25 LTS</wa-tag>
						<wa-tag pill variant="neutral">Maven 3.9+</wa-tag>
					</div>
				</wa-details>
				<wa-divider></wa-divider>
				<div class="wa-cluster wa-gap-s">
					<wa-tag pill variant="neutral">Upstream: https://jwebmp.com/</wa-tag>
					<wa-tag pill variant="neutral">Source: https://github.com/JWebMP/Core</wa-tag>
				</div>
			</div>
		</wa-card>
	</div>
</div>